import React from "react";
import { motion } from "framer-motion";
import { Sparkles, GraduationCap, Leaf, Quote } from "lucide-react";

const PORTRAIT = "/kassie-portrait.jpg";

const CREDENTIALS = [
  { icon: GraduationCap, label: "Certified Past Life &\nSoul Regression Therapist" },
  { icon: Sparkles,      label: "20+ years exploring\nconsciousness & healing" },
  { icon: Leaf,          label: "Gentle deep-state\nhypnosis approach" },
];

export default function AboutSection() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-20 md:py-32 px-5 md:px-12 overflow-hidden"
    >
      {/* Decorative background glows */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-20 -left-20 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(75,0,130,0.18),transparent_65%)]" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.06),transparent_60%)]" />
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Editorial-style heading row */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-6 mb-12 md:mb-16"
        >
          <div>
            <p className="font-sans text-xs tracking-overline text-[#D4AF37] mb-3 md:mb-4">
              04 — about
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white tracking-tight leading-[1.02] md:leading-[0.95]">
              The hands <span className="italic gold-text">that hold</span>
              <br /> the space.
            </h2>
          </div>
          <p className="font-serif italic text-base sm:text-lg text-slate-300 md:max-w-xs md:text-right leading-relaxed">
            "We are spiritual beings having a human experience."
          </p>
        </motion.div>

        {/* Main split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Portrait column */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5 relative"
          >
            {/* Arched frame with gold ring */}
            <div className="relative mx-auto" style={{ maxWidth: 460 }}>
              <div className="absolute -inset-4 rounded-t-[260px] rounded-b-3xl bg-gradient-to-b from-[#D4AF37]/30 via-[#D4AF37]/5 to-transparent blur-2xl" />
              <div className="relative rounded-t-[240px] rounded-b-3xl border border-[#D4AF37]/30 p-[2px] bg-gradient-to-br from-[#D4AF37]/40 via-white/5 to-transparent overflow-hidden">
                <img
                  src={PORTRAIT}
                  alt="Kassie — Past Life & Soul Regression Therapist"
                  className="w-full h-full rounded-t-[238px] rounded-b-3xl object-cover aspect-[4/5]"
                  loading="lazy"
                  data-testid="about-portrait"
                />
              </div>

              {/* Floating sun emblem top */}
              <div className="absolute -top-5 -right-5 w-20 h-20 rounded-full bg-[#0B0E14]/80 backdrop-blur-xl border border-[#D4AF37]/30 flex items-center justify-center shadow-[0_20px_60px_-10px_rgba(212,175,55,0.25)]">
                <div className="relative w-12 h-12 flex items-center justify-center">
                  {[...Array(12)].map((_, i) => (
                    <span
                      key={i}
                      className="absolute w-[2px] h-2 bg-[#D4AF37]/60"
                      style={{ transform: `rotate(${i * 30}deg) translateY(-20px)` }}
                    />
                  ))}
                  <div className="w-2 h-2 rounded-full bg-[#D4AF37] shadow-[0_0_12px_#D4AF37]" />
                </div>
              </div>

              {/* Name tag bottom */}
              <div className="mt-6 flex items-center justify-between">
                <div>
                  <p className="font-serif text-2xl text-white italic leading-tight">Kassie</p>
                  <p className="font-sans text-[11px] uppercase tracking-overline text-[#D4AF37]">Thomas-Smith</p>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400 font-sans">
                  <span className="w-8 h-px bg-[#D4AF37]/40" />
                  Queensland, AU
                </div>
              </div>
            </div>
          </motion.div>

          {/* Copy column */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="space-y-5 md:space-y-6 font-sans text-[15px] sm:text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl"
            >
              <p>
                Kassie is a certified Past Life and Soul Regression Therapist with
                <span className="text-white"> over two decades</span> devoted to exploring consciousness, healing,
                and the soul's journey. Through a gentle deep-state hypnosis approach, she supports clients in
                accessing inner wisdom, subconscious memories, and symbolic insight — facilitating emotional
                release, clarity, and lasting transformation.
              </p>
              <p>
                Through <span className="gold-text font-medium">Kama Loka Journeys</span>, Kassie honours the
                understanding that we are spiritual beings having a human experience. Her work supports the safe
                release of emotional patterns, the cultivation of self-awareness, and a return to each client's
                natural state of balance, authenticity, and soul alignment.
              </p>
            </motion.div>

            {/* Pulled quote */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="my-8 md:my-10 relative pl-5 md:pl-6 border-l border-[#D4AF37]/40"
            >
              <Quote className="absolute -left-3 top-0 w-6 h-6 text-[#D4AF37] bg-[#040508] p-1" />
              <p className="font-serif italic text-xl sm:text-2xl md:text-3xl text-white leading-snug max-w-xl">
                "A return to your natural state of balance, authenticity, and soul alignment."
              </p>
            </motion.div>

            {/* Credentials ribbon */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.35 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-3"
              data-testid="about-credentials"
            >
              {CREDENTIALS.map((c, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 hover:border-[#D4AF37]/30 transition-colors"
                >
                  <c.icon className="w-5 h-5 text-[#D4AF37]" />
                  <p className="mt-3 font-sans text-sm text-slate-200 leading-snug whitespace-pre-line">
                    {c.label}
                  </p>
                </div>
              ))}
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="mt-10 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4"
            >
              <a
                href="#sessions"
                data-testid="about-cta-sessions"
                className="btn-gold rounded-full px-7 py-3.5 text-xs inline-flex items-center justify-center gap-2"
              >
                Begin a session with Kassie
              </a>
              <a
                href="#faq"
                data-testid="about-cta-faq"
                className="btn-ghost-gold rounded-full px-7 py-3.5 text-xs inline-flex items-center justify-center gap-2"
              >
                Questions first? See the FAQ
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
